<template>
    <div class="business-profile-container">
        <div v-if="loading" class="loading-overlay">
            <a-spin size="large" :tip="$t('whatsapp_business_api.loadingProfile')" />
        </div>

        <div v-else class="business-profile-content">
            <!-- Header with Refresh Button -->
            <div class="profile-header-bar">
                <div class="header-left">
                    <h2 class="page-title">{{ $t('whatsapp_business_api.businessProfileTitle') }}</h2>
                    <span v-if="profile.lastSyncedAt" class="last-sync-time">
                        <i class="fa fa-clock-o"></i>
                        {{ $t('whatsapp_business_api.lastUpdate') }} {{ formatDate(profile.lastSyncedAt) }}
                    </span>
                </div>
                <div class="header-right">
                    <a-button
                        type="primary"
                        @click="refreshProfile"
                        :loading="refreshing"
                        :disabled="!canRefresh"
                        size="large"
                    >
                        <i class="fa fa-refresh"></i>
                        {{ $t('whatsapp_business_api.updateFromMeta') }}
                    </a-button>
                    <div v-if="!canRefresh" class="refresh-cooldown">
                        <i class="fa fa-hourglass-half"></i>
                        {{ $t('whatsapp_business_api.waitSeconds', { n: remainingTime }) }}
                    </div>
                </div>
            </div>

            <!-- WABA Status Cards -->
            <div class="waba-status-section">
                <h3 class="section-title">{{ $t('whatsapp_business_api.wabaInfoTitle') }}</h3>

                <!-- Primary Status Cards -->
                <div class="status-cards-primary">
                    <div class="status-card-large">
                        <div class="card-header">
                            <i class="fa fa-phone"></i>
                            <span>{{ $t('whatsapp_business_api.phoneInfo') }}</span>
                        </div>
                        <div class="card-body">
                            <div class="info-row">
                                <span class="info-label">{{ $t('whatsapp_business_api.verifiedNameLabel') }}</span>
                                <span class="info-value verified">{{ profile.wabaInfo.verifiedName }}</span>
                            </div>
                            <div class="info-row">
                                <span class="info-label">{{ $t('whatsapp_business_api.phoneNumberLabel') }}</span>
                                <span class="info-value">{{ profile.wabaInfo.displayPhoneNumber }}</span>
                            </div>
                            <div class="info-row">
                                <span class="info-label">{{ $t('whatsapp_business_api.codeVerificationLabel') }}</span>
                                <span class="info-value" :class="profile.wabaInfo.codeVerificationStatus === 'VERIFIED' ? 'verified' : 'pending'">
                                    {{ profile.wabaInfo.codeVerificationStatus }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <div class="status-card-large">
                        <div class="card-header">
                            <i class="fa fa-info-circle"></i>
                            <span>{{ $t('whatsapp_business_api.accountStatus') }}</span>
                        </div>
                        <div class="card-body">
                            <div class="info-row">
                                <span class="info-label">{{ $t('whatsapp_business_api.accountStatus') }}: </span>
                                <span class="info-value" :class="'status-' + profile.wabaInfo.accountStatus.toLowerCase()">
                                    {{ accountStatus[profile.wabaInfo.accountStatus].text }}
                                </span>
                            </div>
                            <div class="info-row">
                                <span class="info-label">{{ $t('whatsapp_business_api.phoneQuality') }}: </span>
                                <span class="info-value" :class="'quality-' + profile.wabaInfo.phoneQuality.toLowerCase()">
                                    {{ phoneQuality[profile.wabaInfo.phoneQuality].text }}
                                </span>
                            </div>
                            <div class="info-row">
                                <span class="info-label">{{ $t('whatsapp_business_api.accountModeLabel') }}</span>
                                <span class="info-value">{{ profile.wabaInfo.accountMode || $t('whatsapp_business_api.na') }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="status-card-large">
                        <div class="card-header">
                            <i class="fa fa-cog"></i>
                            <span>{{ $t('whatsapp_business_api.platformSettings') }}</span>
                        </div>
                        <div class="card-body">
                            <div class="info-row">
                                <span class="info-label">{{ $t('whatsapp_business_api.platformTypeLabel') }}</span>
                                <span class="info-value">{{ profile.wabaInfo.platformType }}</span>
                            </div>
                            <div class="info-row">
                                <span class="info-label">{{ $t('whatsapp_business_api.throughputLevelLabel') }}</span>
                                <span class="info-value">{{ profile.wabaInfo.throughputLevel }}</span>
                            </div>
                            <div class="info-row">
                                <span class="info-label">{{ $t('whatsapp_business_api.twoFaPinLabel') }}</span>
                                <span class="info-value" :class="profile.wabaInfo.isPinEnabled ? 'verified' : ''">
                                    {{ profile.wabaInfo.isPinEnabled ? $t('whatsapp_business_api.enabled') : $t('whatsapp_business_api.disabled') }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Secondary Status Cards -->
                <div class="status-cards-secondary">
                    <div class="status-mini-card" :class="accountStatus[profile.wabaInfo.accountStatus].color">
                        <i class="fa fa-shield card-icon"></i>
                        <div class="card-content">
                            <div class="card-value">{{ accountStatus[profile.wabaInfo.accountStatus].text }}</div>
                            <div class="card-label">{{ $t('whatsapp_business_api.accountStatus') }}</div>
                        </div>
                    </div>

                    <div class="status-mini-card" :class="phoneQuality[profile.wabaInfo.phoneQuality].color">
                        <i class="fa fa-star card-icon"></i>
                        <div class="card-content">
                            <div class="card-value">{{ phoneQuality[profile.wabaInfo.phoneQuality].text }}</div>
                            <div class="card-label">{{ $t('whatsapp_business_api.phoneQuality') }}</div>
                        </div>
                    </div>

                    <div class="status-mini-card success">
                        <i class="fa fa-paper-plane card-icon"></i>
                        <div class="card-content">
                            <div class="card-value">{{ formatMessagingLimit(profile.wabaInfo.messagingLimit) }}</div>
                            <div class="card-label">{{ $t('whatsapp_business_api.dailyLimit') }}</div>
                        </div>
                    </div>

                    <div class="status-mini-card" :class="canSendMessage[profile.wabaInfo.canSendMessage].color">
                        <i class="fa fa-bolt card-icon"></i>
                        <div class="card-content">
                            <div class="card-value">{{ canSendMessage[profile.wabaInfo.canSendMessage].text }}</div>
                            <div class="card-label">{{ $t('whatsapp_business_api.messageSending') }}</div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Profile Information -->
            <div class="profile-info-section">
                <h3 class="section-title">{{ $t('whatsapp_business_api.businessInfoTitle') }}</h3>

                <div class="profile-grid">
                    <!-- Profile Picture -->
                    <div class="profile-picture-card">
                        <div class="card-label">{{ $t('whatsapp_business_api.profilePhoto') }}</div>
                        <div class="profile-picture">
                            <img :src="profile.profilePictureUrl || '/default-avatar.png'" alt="Profile" />
                            <div class="picture-upload-overlay">
                                <a-upload
                                    :beforeUpload="handleProfilePictureUpload"
                                    :showUploadList="false"
                                    accept="image/*"
                                >
                                    <a-button type="primary" size="small">
                                        <i class="fa fa-camera"></i>
                                        {{ $t('whatsapp_business_api.change') }}
                                    </a-button>
                                </a-upload>
                            </div>
                        </div>
                        <div class="profile-picture-note">
                            {{ $t('whatsapp_business_api.profilePhotoNote') }}
                        </div>
                    </div>

                    <!-- About Text -->
                    <div class="form-field full-width">
                        <label class="field-label">
                            {{ $t('whatsapp_business_api.aboutText') }} <span class="required">*</span>
                        </label>
                        <a-textarea
                            v-model="profile.about"
                            :auto-size="{ minRows: 3, maxRows: 5 }"
                            :placeholder="$t('whatsapp_business_api.aboutPlaceholder')"
                            :maxLength="139"
                            showCount
                        />
                        <div class="field-hint">{{ $t('whatsapp_business_api.aboutHint') }}</div>
                    </div>

                    <!-- Description -->
                    <div class="form-field full-width">
                        <label class="field-label">{{ $t('whatsapp_business_api.businessDescriptionLabel') }}</label>
                        <a-textarea
                            v-model="profile.description"
                            :auto-size="{ minRows: 4, maxRows: 6 }"
                            :placeholder="$t('whatsapp_business_api.businessDescriptionPlaceholder')"
                            :maxLength="512"
                            showCount
                        />
                        <div class="field-hint">{{ $t('whatsapp_business_api.descriptionHint') }}</div>
                    </div>

                    <!-- Email -->
                    <div class="form-field">
                        <label class="field-label">{{ $t('whatsapp_business_api.contactEmail') }}</label>
                        <a-input
                            v-model="profile.email"
                            :placeholder="$t('whatsapp_business_api.emailPlaceholder')"
                            type="email"
                        >
                            <i slot="prefix" class="fa fa-envelope"></i>
                        </a-input>
                    </div>

                    <!-- Address -->
                    <div class="form-field">
                        <label class="field-label">{{ $t('whatsapp_business_api.businessAddress') }}</label>
                        <a-input
                            v-model="profile.address"
                            :placeholder="$t('whatsapp_business_api.addressPlaceholder')"
                        >
                            <i slot="prefix" class="fa fa-map-marker"></i>
                        </a-input>
                    </div>

                    <!-- Website 1 -->
                    <div class="form-field">
                        <label class="field-label">{{ $t('whatsapp_business_api.website1') }}</label>
                        <a-input
                            v-model="websiteInputs.website1"
                            @change="updateWebsites"
                            :placeholder="$t('whatsapp_business_api.website1Placeholder')"
                        >
                            <i slot="prefix" class="fa fa-globe"></i>
                        </a-input>
                    </div>

                    <!-- Website 2 -->
                    <div class="form-field">
                        <label class="field-label">{{ $t('whatsapp_business_api.website2') }}</label>
                        <a-input
                            v-model="websiteInputs.website2"
                            @change="updateWebsites"
                            :placeholder="$t('whatsapp_business_api.website2Placeholder')"
                        >
                            <i slot="prefix" class="fa fa-globe"></i>
                        </a-input>
                    </div>

                    <!-- Vertical -->
                    <div class="form-field full-width">
                        <label class="field-label">
                            {{ $t('whatsapp_business_api.businessCategory') }} <span class="required">*</span>
                        </label>
                        <a-select
                            v-model="profile.vertical"
                            size="large"
                            :placeholder="$t('whatsapp_business_api.selectBusinessCategory')"
                        >
                            <a-select-option
                                v-for="vertical of verticals"
                                :value="vertical.value"
                                :key="vertical.value"
                            >
                                {{ vertical.label }}
                            </a-select-option>
                        </a-select>
                        <div class="field-hint">{{ $t('whatsapp_business_api.categoryHint') }}</div>
                    </div>
                </div>
            </div>

            <!-- Action Buttons -->
            <div class="action-bar" v-if="hasChanges">
                <a-button size="large" @click="resetForm">
                    <i class="fa fa-undo"></i>
                    {{ $t('whatsapp_business_api.revertChanges') }}
                </a-button>
                <a-button
                    type="primary"
                    size="large"
                    @click="saveProfile"
                    :loading="isSaving"
                >
                    <i class="fa fa-save"></i>
                    {{ $t('whatsapp_business_api.updateButton') }}
                </a-button>
            </div>
        </div>
    </div>
</template>
<script>
import _ from "lodash";
import moment from "moment";
import axios from "axios";
import log2 from "../../../../utils/log2";
import { sendSocketMessage, socketMessageStream } from "../../../../utils/socketStream";
import { uploadAsset } from "../../../../api/assetUpload";
import SupsisButton from "../../Settings/components/SupsisButton.vue";
import { mapState } from "vuex";

const log = log2("BusinessProfile");

// 5 dakika = 300000 ms
const REFRESH_COOLDOWN = 5 * 60 * 1000;

export default {
    name: "BusinessProfile",
    components: { SupsisButton },
    props: {
        value: {
            required: true,
        },
    },
    data() {
        return {
            loading: true,
            isSaving: false,
            refreshing: false,
            lastRefreshTime: null,
            remainingTime: 0,
            refreshTimer: null,
            websiteInputs: {
                website1: "",
                website2: "",
            },
            profile: {
                profilePictureUrl: "",
                uploadedProfilePictureUrl: null,
                about: "",
                address: "",
                description: "",
                email: "",
                websites: [],
                vertical: "OTHER",
                wabaInfo: {
                    accountStatus: "UNKNOWN",
                    phoneQuality: "UNKNOWN",
                    messagingLimit: "TIER_250",
                    canSendMessage: "AVAILABLE",
                    verifiedName: "",
                    displayPhoneNumber: "",
                },
                lastSyncedAt: null,
            },
            clone: _.cloneDeep(this.value),
            subscribers: [],
            verticalValues: [
                { value: "OTHER" },
                { value: "AUTO" },
                { value: "BEAUTY" },
                { value: "APPAREL" },
                { value: "EDU" },
                { value: "ENTERTAIN" },
                { value: "EVENT_PLAN" },
                { value: "FINANCE" },
                { value: "GROCERY" },
                { value: "GOVT" },
                { value: "HOTEL" },
                { value: "HEALTH" },
                { value: "NONPROFIT" },
                { value: "PROF_SERVICES" },
                { value: "RETAIL" },
                { value: "TRAVEL" },
                { value: "RESTAURANT" },
                { value: "ALCOHOL" },
                { value: "ONLINE_GAMBLING" },
                { value: "PHYSICAL_GAMBLING" },
                { value: "OTC_DRUGS" },
                { value: "MATRIMONY_SERVICE" },
            ],
        };
    },
    computed: {
        ...mapState({
            site: state => state.site.site,
        }),
        accountStatus() {
            return {
                ACTIVE: { color: "SUCCESS", text: this.$t("whatsapp_business_api.active") },
                BANNED: { color: "DANGER", text: this.$t("whatsapp_business_api.banned") },
                RESTRICTED: { color: "WARNING", text: this.$t("whatsapp_business_api.restricted") },
                FLAGGED: { color: "DANGER", text: this.$t("whatsapp_business_api.flagged") },
                UNKNOWN: { color: "DANGER", text: this.$t("whatsapp_business_api.unknown") },
            };
        },
        phoneQuality() {
            return {
                UNKNOWN: { color: "DANGER", text: this.$t("whatsapp_business_api.unknown") },
                RED: { color: "DANGER", text: this.$t("whatsapp_business_api.low") },
                YELLOW: { color: "WARNING", text: this.$t("whatsapp_business_api.medium") },
                GREEN: { color: "SUCCESS", text: this.$t("whatsapp_business_api.high") },
            };
        },
        canSendMessage() {
            return {
                AVAILABLE: { color: "SUCCESS", text: this.$t("whatsapp_business_api.available") },
                LIMITED: { color: "WARNING", text: this.$t("whatsapp_business_api.limited") },
                BLOCKED: { color: "DANGER", text: this.$t("whatsapp_business_api.blocked") },
            };
        },
        verticals() {
            var self = this;
            return this.verticalValues.map(function(v) {
                var key = "vertical" + v.value.split("_").map(function(p) {
                    return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
                }).join("");
                return { value: v.value, label: self.$t("whatsapp_business_api." + key) };
            });
        },
        canRefresh() {
            if (!this.lastRefreshTime) return true;
            const now = Date.now();
            return now - this.lastRefreshTime >= REFRESH_COOLDOWN;
        },
        hasChanges() {
            if (!this.value.businessProfile) return false;
            const original = this.value.businessProfile;
            return (
                this.profile.about !== (original.about || "") ||
                this.profile.description !== (original.description || "") ||
                this.profile.email !== (original.email || "") ||
                this.profile.address !== (original.address || "") ||
                this.profile.vertical !== (original.vertical || "OTHER") ||
                this.websiteInputs.website1 !== (original.websites[0] || "") ||
                this.websiteInputs.website2 !== (original.websites[1] || "") ||
                !!this.profile.uploadedProfilePictureUrl
            );
        },
    },
    mounted() {
        this.loadProfile();

        // Socket subscription for profile updates
        const profileSub = socketMessageStream.subscribe((message) => {
            if (message.cmd === "whatsapp_business_profile") {
                if (message.data.channelId === this.value._id) {
                    this.handleProfileData(message.data);
                }
            } else if (message.cmd === "whatsapp_business_profile_updated") {
                if (message.data.success) {
                    this.$notification.success({
                        message: "Başarılı",
                        description: "Profil bilgileri güncellendi",
                    });
                } else {
                    this.$notification.error({
                        message: "Hata",
                        description: message.data.error || "Güncelleme başarısız",
                    });
                }
                this.isSaving = false;
                // Clear uploaded profile picture URL after save
                this.profile.uploadedProfilePictureUrl = null;
            }
        });

        this.subscribers.push(() => {
            profileSub.unsubscribe();
        });

        // Refresh timer için countdown
        this.refreshTimer = setInterval(() => {
            if (this.lastRefreshTime) {
                const elapsed = Date.now() - this.lastRefreshTime;
                const remaining = Math.max(0, REFRESH_COOLDOWN - elapsed);
                this.remainingTime = Math.ceil(remaining / 1000);
            }
        }, 1000);
    },
    beforeDestroy() {
        this.subscribers.forEach((fn) => fn());
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
        }
    },
    methods: {
        formatDate(date) {
            if (!date) return "-";
            return moment(date).format("DD.MM.YYYY HH:mm");
        },
        formatMessagingLimit(limit) {
            if (!limit) return "N/A";
            const number = limit.split("_")[1];
            if (number === "UNLIMITED") return "Sınırsız";
            return number;
        },
        resetForm() {
            if (this.value.businessProfile) {
                this.setProfileFromDB(this.value.businessProfile);
                this.$message.info("Değişiklikler geri alındı");
            }
        },
        async handleProfilePictureUpload(file) {
            // Validate file
            const isImage = file.type.startsWith("image/");
            if (!isImage) {
                this.$message.error("Sadece resim dosyası yükleyebilirsiniz!");
                return false;
            }

            const isLt5M = file.size / 1024 / 1024 < 5;
            if (!isLt5M) {
                this.$message.error("Resim boyutu 5MB'dan küçük olmalıdır!");
                return false;
            }

            try {
                this.$message.loading({ content: "Profil fotoğrafı yükleniyor...", key: "uploadProfilePic" });

                const path = `${this.site._id}/whatsapp-profile/${this.value._id}/`;

                // Upload to S3
                const uploadData = await uploadAsset({
                    path,
                    name: file.name,
                    size: file.size,
                    type: "whatsapp-profile-picture",
                    mime: file.type,
                    ext: file.name.split(".").pop(),
                });

                await axios({
                    method: uploadData.data.method,
                    url: uploadData.data.url,
                    data: file,
                    headers: { "Content-Type": "application/octet-stream" },
                });

                // Get asset URL
                const asset = uploadData.data.asset;
                const imageUrl = asset.v2Format && asset.cdnUrl
                    ? asset.cdnUrl
                    : `https://static.supsis.live/${asset.path}${asset._id}.${asset.ext}`;

                // Update local preview immediately
                this.profile.profilePictureUrl = imageUrl;
                this.profile.uploadedProfilePictureUrl = imageUrl;
            } catch (error) {
                log("Profile picture upload error", error);
                this.$message.error({ content: "Profil fotoğrafı yüklenemedi!", key: "uploadProfilePic" });
            }

            return false; // Prevent default upload
        },
        async loadProfile() {
            this.loading = true;

            // Kanal detayında businessProfile populate edilmiş olarak gelir
            if (this.value.businessProfile) {
                // DB'de profil var, direkt kullan
                this.setProfileFromDB(this.value.businessProfile);
                this.loading = false;
            } else {
                // DB'de profil yok, Meta'dan çek
                this.fetchFromMeta();
            }
        },
        fetchFromMeta() {
            log("Fetching profile from Meta API");
            sendSocketMessage("whatsapp profile", {
                siteId: this.value.siteId,
                channelId: this.value._id,
                channelType: this.value.type,
            });
        },
        setProfileFromDB(profileData) {
            log("Setting profile from DB", profileData);
            const websites = profileData.websites || [];
            this.websiteInputs = {
                website1: websites[0] || "",
                website2: websites[1] || "",
            };
            this.profile = {
                profilePictureUrl: profileData.profilePictureUrl || "",
                uploadedProfilePictureUrl: null,
                about: profileData.about || "",
                address: profileData.address || "",
                description: profileData.description || "",
                email: profileData.email || "",
                websites: websites,
                vertical: profileData.vertical || "OTHER",
                wabaInfo: profileData.wabaInfo || this.profile.wabaInfo,
                lastSyncedAt: profileData.lastSyncedAt || null,
            };
        },
        updateWebsites() {
            const websites = [];
            if (this.websiteInputs.website1 && this.websiteInputs.website1.trim()) {
                websites.push(this.websiteInputs.website1.trim());
            }
            if (this.websiteInputs.website2 && this.websiteInputs.website2.trim()) {
                websites.push(this.websiteInputs.website2.trim());
            }
            this.profile.websites = websites;
        },
        handleProfileData(data) {
            if (data.error) {
                this.$notification.error({
                    message: "Hata",
                    description: data.error,
                });
                this.loading = false;
                this.refreshing = false;
                return;
            }

            const profileData = data.profile;
            const websites = profileData.profile?.websites || [];

            // Map profile data to component structure
            this.websiteInputs = {
                website1: websites[0] || "",
                website2: websites[1] || "",
            };

            this.profile = {
                profilePictureUrl: profileData.profile?.profile_picture_url || "",
                uploadedProfilePictureUrl: null,
                about: profileData.profile?.about || "",
                address: profileData.profile?.address || "",
                description: profileData.profile?.description || "",
                email: profileData.profile?.email || "",
                websites: websites,
                vertical: profileData.profile?.vertical || "OTHER",
                wabaInfo: profileData.wabaInfo || this.profile.wabaInfo,
                lastSyncedAt: new Date(),
            };

            this.loading = false;
            this.refreshing = false;

            this.$notification.success({
                message: "Başarılı",
                description: "Profil bilgileri güncellendi",
            });
        },
        async refreshProfile() {
            if (!this.canRefresh) {
                this.$message.warning(`Lütfen ${this.remainingTime} saniye bekleyin`);
                return;
            }

            this.refreshing = true;
            this.lastRefreshTime = Date.now();

            // Meta'dan güncel bilgiyi çek
            sendSocketMessage("whatsapp profile", {
                siteId: this.value.siteId,
                channelId: this.value._id,
                channelType: this.value.type,
            });
        },
        async saveProfile() {
            this.isSaving = true;

            const payload = {
                about: this.profile.about,
                address: this.profile.address,
                description: this.profile.description,
                email: this.profile.email,
                websites: this.profile.websites,
                vertical: this.profile.vertical,
            };

            const requestData = {
                siteId: this.value.siteId,
                channelId: this.value._id,
                channelType: this.value.type,
                payload: payload,
            };

            // Only add profilePictureUrl if user uploaded a new one
            if (this.profile.uploadedProfilePictureUrl) {
                requestData.profilePictureUrl = this.profile.uploadedProfilePictureUrl;
            }

            await sendSocketMessage("whatsapp update-profile", requestData);
        },
    },
    watch: {
        clone: {
            handler: function (val) {
                if (val) {
                    const isChanged = !_.isEqual(val, this.value);
                    if (isChanged) {
                        log("clone changed=>", val);
                        this.$emit("input", val);
                    }
                }
            },
            deep: true,
        },
        value: {
            handler: function (val) {
                const isChanged = !_.isEqual(val, this.clone);
                if (isChanged) {
                    log("value changed=>", val);
                    this.clone = _.cloneDeep(val);
                }
            },
            deep: true,
        },
    },
};
</script>

<style scoped lang="scss">
// Color Variables
$primary-dark: #22272d;
$primary-purple: #46499D;
$light-purple: #EEE7FF;
$light-gray: #f1f4f7;
$medium-gray: #c7d0d7;
$success-color: #52c41a;
$warning-color: #faad14;
$danger-color: #ff4d4f;

.business-profile-container {
    width: 100%;
    min-height: 100vh;
    background: $light-gray;
    position: relative;
}

.loading-overlay {
    width: 100%;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.95);
    position: absolute;
    top: 0;
    left: 0;
    z-index: 999;
}

.business-profile-content {
    max-width: 1400px;
    margin: 0 auto;
    padding: 24px;
}

// Header Bar
.profile-header-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
    padding: 20px 24px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(34, 39, 45, 0.08);

    .header-left {
        display: flex;
        flex-direction: column;
        gap: 6px;

        .page-title {
            margin: 0;
            font-size: 24px;
            font-weight: 600;
            color: $primary-dark;
        }

        .last-sync-time {
            font-size: 13px;
            color: $primary-purple;
            display: flex;
            align-items: center;
            gap: 6px;

            i {
                color: $primary-purple;
            }
        }
    }

    .header-right {
        display: flex;
        align-items: center;
        gap: 12px;

        .ant-btn-primary {
            background: $primary-purple;
            border-color: $primary-purple;
            height: 40px;
            padding: 0 24px;
            font-weight: 500;

            i {
                margin-right: 8px;
            }

            &:hover:not(:disabled) {
                background: darken($primary-purple, 8%);
                border-color: darken($primary-purple, 8%);
            }

            &:disabled {
                background: $medium-gray;
                border-color: $medium-gray;
                color: #999;
            }
        }

        .refresh-cooldown {
            display: flex;
            align-items: center;
            gap: 6px;
            padding: 8px 16px;
            background: $light-purple;
            border-radius: 8px;
            color: $primary-purple;
            font-size: 13px;
            font-weight: 500;

            i {
                color: $primary-purple;
            }
        }
    }
}

// WABA Status Section
.waba-status-section {
    margin-bottom: 24px;

    .section-title {
        margin: 0 0 20px 0;
        font-size: 18px;
        font-weight: 600;
        color: $primary-dark;
    }

    // Primary Large Cards
    .status-cards-primary {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
        gap: 16px;
        margin-bottom: 16px;
    }

    .status-card-large {
        background: white;
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 2px 8px rgba(34, 39, 45, 0.08);
        transition: all 0.3s ease;

        &:hover {
            box-shadow: 0 4px 16px rgba(34, 39, 45, 0.12);
        }

        .card-header {
            background: linear-gradient(135deg, $primary-purple 0%, darken($primary-purple, 10%) 100%);
            color: white;
            padding: 16px 20px;
            display: flex;
            align-items: center;
            gap: 10px;
            font-weight: 600;
            font-size: 15px;

            i {
                font-size: 18px;
            }
        }

        .card-body {
            padding: 20px;
            display: flex;
            flex-direction: column;
            gap: 14px;

            .info-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding-bottom: 10px;
                border-bottom: 1px solid $light-gray;

                &:last-child {
                    border-bottom: none;
                    padding-bottom: 0;
                }

                .info-label {
                    font-size: 13px;
                    color: $primary-purple;
                    font-weight: 500;
                }

                .info-value {
                    font-size: 14px;
                    color: $primary-dark;
                    font-weight: 600;

                    &.verified {
                        color: $success-color;
                    }

                    &.pending {
                        color: $warning-color;
                    }

                    &.status-active {
                        color: $success-color;
                    }

                    &.status-banned,
                    &.status-restricted,
                    &.status-flagged {
                        color: $danger-color;
                    }

                    &.quality-green {
                        color: $success-color;
                    }

                    &.quality-yellow {
                        color: $warning-color;
                    }

                    &.quality-red,
                    &.quality-unknown {
                        color: $danger-color;
                    }
                }
            }
        }
    }

    // Secondary Mini Cards
    .status-cards-secondary {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
        gap: 12px;
    }

    .status-mini-card {
        background: white;
        border-radius: 10px;
        padding: 16px 18px;
        display: flex;
        align-items: center;
        gap: 14px;
        box-shadow: 0 2px 6px rgba(34, 39, 45, 0.06);
        border-left: 4px solid;
        transition: all 0.3s ease;

        &.success,
        &.SUCCESS {
            border-left-color: $success-color;

            .card-icon {
                color: $success-color;
            }
        }

        &.warning,
        &.WARNING {
            border-left-color: $warning-color;

            .card-icon {
                color: $warning-color;
            }
        }

        &.danger,
        &.DANGER {
            border-left-color: $danger-color;

            .card-icon {
                color: $danger-color;
            }
        }

        &.UNKNOWN {
            border-left-color: $medium-gray;

            .card-icon {
                color: $medium-gray;
            }
        }

        &:hover {
            transform: translateX(4px);
            box-shadow: 0 3px 10px rgba(34, 39, 45, 0.1);
        }

        .card-icon {
            font-size: 28px;
        }

        .card-content {
            flex: 1;

            .card-value {
                font-size: 18px;
                font-weight: 600;
                color: $primary-dark;
                margin-bottom: 2px;
            }

            .card-label {
                font-size: 12px;
                color: $primary-purple;
                font-weight: 500;
            }
        }
    }
}

// Profile Info Section
.profile-info-section {
    margin-bottom: 24px;

    .section-title {
        margin: 0 0 16px 0;
        font-size: 18px;
        font-weight: 600;
        color: $primary-dark;
    }

    .profile-grid {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 20px;
        background: white;
        border-radius: 12px;
        padding: 24px;
        box-shadow: 0 2px 8px rgba(34, 39, 45, 0.08);

        .profile-picture-card {
            grid-column: span 2;
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 12px;
            padding: 20px;
            background: $light-gray;
            border-radius: 12px;

            .card-label {
                font-size: 14px;
                font-weight: 600;
                color: $primary-dark;
            }

            .profile-picture {
                width: 120px;
                height: 120px;
                border-radius: 50%;
                overflow: hidden;
                border: 4px solid white;
                box-shadow: 0 4px 12px rgba(34, 39, 45, 0.15);
                position: relative;
                cursor: pointer;

                img {
                    width: 100%;
                    height: 100%;
                    object-fit: cover;
                }

                .picture-upload-overlay {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                    background: rgba(34, 39, 45, 0.7);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    opacity: 0;
                    transition: opacity 0.3s ease;

                    .ant-btn {
                        background: $primary-purple;
                        border-color: $primary-purple;

                        &:hover {
                            background: darken($primary-purple, 10%);
                            border-color: darken($primary-purple, 10%);
                        }
                    }
                }

                &:hover .picture-upload-overlay {
                    opacity: 1;
                }
            }

            .profile-picture-note {
                font-size: 12px;
                color: $primary-purple;
                text-align: center;
            }
        }

        .form-field {
            display: flex;
            flex-direction: column;
            gap: 8px;

            &.full-width {
                grid-column: span 2;
            }

            .field-label {
                font-size: 14px;
                font-weight: 600;
                color: $primary-dark;
                margin-bottom: 4px;

                .required {
                    color: $danger-color;
                    margin-left: 2px;
                }
            }

            .field-hint {
                font-size: 12px;
                color: $primary-purple;
                margin-top: 4px;
            }

            input,
            textarea {
                border-radius: 8px;
                border-color: $medium-gray;
                font-size: 14px;

                &:hover {
                    border-color: $primary-purple;
                }

                &:focus {
                    border-color: $primary-purple;
                    box-shadow: 0 0 0 2px rgba(100, 116, 145, 0.1);
                }
            }

            .ant-select {
                .ant-select-selection {
                    border-radius: 8px;
                    border-color: $medium-gray;

                    &:hover {
                        border-color: $primary-purple;
                    }
                }

                &.ant-select-focused .ant-select-selection {
                    border-color: $primary-purple;
                    box-shadow: 0 0 0 2px rgba(100, 116, 145, 0.1);
                }
            }
        }
    }
}

// Action Bar
.action-bar {
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    padding: 16px 24px;
    background: white;
    border-top: 1px solid $medium-gray;
    box-shadow: 0 -2px 12px rgba(34, 39, 45, 0.08);
    z-index: 10;

    .ant-btn {
        height: 40px;
        padding: 0 28px;
        font-weight: 500;
        border-radius: 8px;
        transition: all 0.2s ease;

        i {
            margin-right: 8px;
        }

        &.ant-btn-primary {
            background: $primary-purple;
            border-color: $primary-purple;

            &:hover:not(:disabled) {
                background: darken($primary-purple, 8%);
                border-color: darken($primary-purple, 8%);
            }
        }

        &:not(.ant-btn-primary) {
            background: white;
            border-color: $medium-gray;
            color: $primary-purple;

            &:hover {
                border-color: $primary-purple;
                background: lighten($light-purple, 5%);
            }
        }
    }
}

// Responsive
@media (max-width: 1024px) {
    .waba-status-section {
        .status-cards-primary {
            grid-template-columns: 1fr;
        }

        .status-cards-secondary {
            grid-template-columns: repeat(2, 1fr);
        }
    }
}

@media (max-width: 768px) {
    .business-profile-content {
        padding: 16px;
    }

    .profile-header-bar {
        flex-direction: column;
        align-items: flex-start;
        gap: 16px;
        padding: 16px;

        .header-left .page-title {
            font-size: 20px;
        }

        .header-right {
            width: 100%;
            flex-direction: column;

            .ant-btn {
                width: 100%;
            }
        }
    }

    .profile-info-section .profile-grid {
        grid-template-columns: 1fr;
        padding: 16px;
        gap: 16px;

        .profile-picture-card {
            grid-column: span 1;
        }

        .form-field.full-width {
            grid-column: span 1;
        }
    }

    .waba-status-section {
        .status-cards-primary {
            grid-template-columns: 1fr;
        }

        .status-cards-secondary {
            grid-template-columns: 1fr;
        }
    }

    .action-bar {
        flex-direction: column;
        align-items: stretch;

        .ant-btn {
            width: 100%;
        }
    }
}
</style>




